import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useExternalLink } from '../components/ExternalLinkWarning';
import PageHeader from '../components/PageHeader';
import usePageSearchIndex from '../hooks/usePageSearchIndex';
import './SiteMap.css';

const SiteMap = () => {
  const { isMarathi } = useLanguage();
  const { showWarning } = useExternalLink();

  // Auto-index all visible content on the Site Map page for search
  usePageSearchIndex({
    mr: ['साइट मॅप', 'मुख्य पृष्ठे', 'सेवा आणि संसाधने', 'मीडिया आणि कार्यक्रम', 'बाह्य लिंक्स्'],
    en: ['site map', 'main pages', 'services and resources', 'media and programs', 'external links']
  });

  // Handle external link clicks
  const handleExternalLink = (e, url) => {
    e.preventDefault();
    showWarning(url);
  };

  const pageHeaderData = {
    title: {
      marathi: 'साइट मॅप',
      english: 'Site Map'
    },
    subtitle: {
      marathi: 'महाराष्ट्र कामगार कल्याण मंडळ वेबसाइटच्या सर्व पृष्ठांची यादी',
      english: 'Complete list of all pages on the Maharashtra Labour Welfare Board website'
    }
  };

  // Organized into three columns like the image example
  const siteMapColumns = [
    {
      title: isMarathi() ? 'मुख्य पृष्ठे' : 'Main Pages',
      links: [
        { path: '/', label: isMarathi() ? 'मुख्यपृष्ठ' : 'Home' },
        { path: '/overview', label: isMarathi() ? 'आढावा' : 'Overview' },
        { path: '/administrative-structure', label: isMarathi() ? 'प्रशासकिय रचना' : 'Administrative Structure' },
        { path: '/board-members', label: isMarathi() ? 'मंडळ सदस्य' : 'Board Members' },
        { path: '/board-project', label: isMarathi() ? 'मंडळ प्रकल्प' : 'Board Project' },
        { path: '/annual-report', label: isMarathi() ? 'वार्षिक गोषवारा' : 'Annual Report' },
        { path: '/circulars', label: isMarathi() ? 'कर्मचाऱ्यांसाठी परिपत्रके' : 'Circulars' },
        { path: '/workers-welfare', label: isMarathi() ? 'कामगार कल्याण निधी' : 'Labours\' Welfare Fund' },
        { path: '/important-circulars', label: isMarathi() ? 'महत्वाची परिपत्रके' : 'Important Circulars' }
      ]
    },
    {
      title: isMarathi() ? 'सेवा आणि संसाधने' : 'Services & Resources',
      links: [
        { path: '/list-of-services', label: isMarathi() ? 'सेवांची यादी' : 'List of Services' },
        { path: '/dashboard', label: isMarathi() ? 'डॅशबोर्ड' : 'Dashboard' },
        { path: '/right-to-info', label: isMarathi() ? 'माहितीचा अधिकार' : 'Right to Information' },
        { path: '/right-to-service', label: isMarathi() ? 'सेवेचा अधिकार' : 'Right to Service' },
        { path: '/feedback', label: isMarathi() ? 'अभिप्राय' : 'Feedback' },
        { path: '/grievance', label: isMarathi() ? 'तक्रार निवारण' : 'Grievance' },
        { path: '/faq', label: isMarathi() ? 'प्रश्नोत्तरे' : 'FAQ' },
        { path: '/contact', label: isMarathi() ? 'संपर्क' : 'Contact' },
        { path: '/privacy', label: isMarathi() ? 'गोपनीयता धोरण' : 'Privacy Policy' }
      ]
    },
    {
      title: isMarathi() ? 'मीडिया आणि कार्यक्रम' : 'Media & Programs',
      links: [
        { path: '/photo-gallery', label: isMarathi() ? 'फोटो गॅलरी' : 'Photo Gallery' },
        { path: '/video-gallery', label: isMarathi() ? 'व्हिडिओ गॅलरी' : 'Video Gallery' },
        { path: '/100-days-program', label: isMarathi() ? '१०० दिवस कार्यक्रम' : '100 Days Program' },
        { path: '/activities', label: isMarathi() ? 'क्रियाकलाप' : 'Activities' },
        { path: '/programs', label: isMarathi() ? 'कार्यक्रम' : 'Programs' },
        { path: '/schemes', label: isMarathi() ? 'योजना' : 'Schemes' },
        { path: '/awards', label: isMarathi() ? 'पुरस्कार' : 'Awards' },
        { path: '/tenders-notices', label: isMarathi() ? 'निविदा व सूचना' : 'Tenders & Notices' },
        { path: '/screen-reader', label: isMarathi() ? 'स्क्रीन रीडर' : 'Screen Reader' }
      ]
    }
  ];

  // External links section
  const externalLinks = [
    { url: 'https://public.mlwb.in/public', label: isMarathi() ? 'सार्वजनिक पोर्टल' : 'Public Portal' },
    { url: 'https://www.india.gov.in/', label: isMarathi() ? 'भारताचे राष्ट्रीय पोर्टल' : 'National Portal of India' },
    { url: 'https://maharashtra.gov.in/home/index', label: isMarathi() ? 'महाराष्ट्र शासन' : 'Maharashtra Government' },
    { url: 'https://mahadish.in/marathi/home', label: isMarathi() ? 'औद्योगिक सुरक्षा व आरोग्य' : 'Industrial Safety & Health' },
    { url: 'https://vvgnli.gov.in/', label: isMarathi() ? 'वी. वी. गिरि राष्ट्रीय श्रम संस्थान' : 'V. V. Giri National Labour Institute' },
    { url: 'https://mahabocw.in/', label: isMarathi() ? 'बांधकाम कामगार कल्याण मंडळ' : 'Construction Workers Welfare Board' },
    { url: 'https://aaplesarkar.mahaonline.gov.in', label: isMarathi() ? 'आपले सरकार' : 'Aaple Sarkar' },
    { url: 'https://adminconsole.mlwb.in/', label: isMarathi() ? 'प्रशासक लॉगिन' : 'Admin Login' }
  ];

  return (
    <div className="sitemap-container">
      {/* Page Header */}
      <PageHeader 
        title={pageHeaderData.title}
        subtitle={pageHeaderData.subtitle}
      />

      {/* Main Sitemap Section - Three Columns */}
      <div className="sitemap-main-section" data-read-section="sitemap-content">
        <h2 className="sitemap-main-title">
          {isMarathi() ? 'साइट मॅप' : 'Site Map'}
        </h2>
        <div className="sitemap-columns">
          {siteMapColumns.map((column, colIndex) => (
            <div key={colIndex} className="sitemap-column">
              <h3 className="sitemap-column-title">{column.title}</h3>
              <ul className="sitemap-links-list">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link to={link.path} className="sitemap-link">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* External Links Section */}
      <div className="sitemap-external-section" data-read-section="sitemap-content">
        <h3 className="sitemap-external-title">
          {isMarathi() ? 'बाह्य लिंक्स्' : 'External Links'}
        </h3>
        <ul className="sitemap-external-links">
          {externalLinks.map((link, index) => (
            <li key={index}>
              <a 
                href={link.url} 
                className="sitemap-external-link"
                onClick={(e) => handleExternalLink(e, link.url)}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SiteMap;
